import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import {
  createVehicleTrackingAttachment,
  getVehicleTrackingAttachments,
  deleteVehicleTrackingAttachment,
} from 'src/redux/Actions/VehicleTracking';
import Table from 'src/components/VehicleTrackingDetails/Tables/Table';
import { ReactComponent as DeleteIcon } from 'src/Images/icons/common/delete.svg';
import { ReactComponent as EyeIcon } from 'src/Images/icons/common/eye.svg';
import { ReactComponent as RefreshIcon } from 'src/Images/icons/common/refresh.svg';
import UploadInvoiceComponent from 'src/components/VehicleTrackingDetails/Invoice/UploadInvoiceComponent';
import 'src/style/invoice.css';
const FILE_TYPE_INVOICE = 1;
export default function InvoiceTab() {
  const dispatch = useDispatch();
  const { vehicleTrackingNumber, workflowStageId } = useParams();
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [loading, setLoading] = useState(false);

  const fileInputRef = useRef(null);

  const authReducer = useSelector((state) => state.AuthReducer);
  const tenantOrganisationId = authReducer.userData.organisations?.id;
  const themeColor = authReducer.userData.tenant?.theme;

  const { invoiceAttachments, vehicleTrackingDetails } = useSelector(
    (state) => state.VehicleTrackingReducer,
  );
  const invoices = invoiceAttachments || [];

  const requestedStage = vehicleTrackingDetails?.requestedStage;
  const canUploadInvoice =
    requestedStage?.allow_vehicle_content_additon &&
    vehicleTrackingDetails?.direction?.id === 1 &&
    !requestedStage?.completed &&
    requestedStage?.entry_log_exist;

  // --- Fetch invoice attachments ---
  const fetchInvoiceAttachments = useCallback(() => {
    if (loading) return;
    setLoading(true);
    dispatch(
      getVehicleTrackingAttachments(
        tenantOrganisationId,
        vehicleTrackingNumber,
        FILE_TYPE_INVOICE,
      ),
    ).finally(() => {
      setLoading(false);
    });
  }, [dispatch, tenantOrganisationId, vehicleTrackingNumber]);

  useEffect(() => {
    fetchInvoiceAttachments();
  }, [fetchInvoiceAttachments]);

  // --- Upload ---
  const handleFileUpload = async (files) => {
    try {
      setUploading(true);
      setUploadMessage('');

      const formData = new FormData();
      files.forEach((file) => formData.append('file', file));
      formData.append('tenant_organisation_id', tenantOrganisationId);
      formData.append('vehicle_tracking_number', vehicleTrackingNumber);
      formData.append('file_type', FILE_TYPE_INVOICE);
      if (workflowStageId) {
        formData.append('workflow_stage_id', workflowStageId);
      }

      await dispatch(createVehicleTrackingAttachment(formData));
      setUploadMessage(
        `${files.length > 1 ? `${files.length} invoices` : 'Invoice'} uploaded! Processing in background...`,
      );
      fetchInvoiceAttachments();
    } catch (error) {
      setUploadMessage(
        error.response?.data?.error ||
          error.message ||
          'Failed to upload invoice. Please try again.',
      );
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      handleFileUpload(files);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // --- Remove ---
  const handleRemoveConfirm = async () => {
    const attachmentId = confirmRemoveId;
    setConfirmRemoveId(null);
    try {
      await dispatch(
        deleteVehicleTrackingAttachment(tenantOrganisationId, attachmentId),
      );
      fetchInvoiceAttachments();
    } catch (error) {
      setErrorMessage('Failed to remove invoice. Please try again.');
    }
  };

  // --- Preview ---
  const handlePreview = (url) => {
    window.open(url, '_blank');
  };

  // --- Status badge ---
  const getStatusBadge = (status, statusDisplay) => {
    const statusConfig = {
      1: { pillClass: 'yellow', text: statusDisplay || 'Pending' },
      2: { pillClass: 'blue', text: statusDisplay || 'Processing' },
      3: { pillClass: 'green', text: statusDisplay || 'Completed' },
      4: { pillClass: 'red', text: statusDisplay || 'Failed' },
    };
    const config = statusConfig[status] || statusConfig[1];
    return (
      <span
        className={`status-pill ${config.pillClass} text-xs text-semi-bold`}
      >
        {config.text}
      </span>
    );
  };

  const hasInvoices = invoices.length > 0;

  const invoiceTableHeaders = [
    { label: 'File', value: 'filename' },
    { label: 'Invoice #', value: 'invoice_number' },
    { label: 'Supplier', value: 'supplier_name' },
    { label: 'Status', value: 'status' },
    { label: 'Actions', value: 'actions' },
  ];

  const renderTableContent = () => (
    <tbody>
      {invoices.map((invoice) => (
        <tr key={invoice.attachment_id}>
          <td className="table-data">
            <div className="div-flex-column rg-5">
              <span className="text-sm text-bold" title={invoice.filename}>
                {invoice.filename}
              </span>
              <span className="text-xs text-grey">
                {new Date(invoice.created).toLocaleDateString()}
              </span>
            </div>
          </td>
          <td className="table-data">
            <span className="text-sm">{invoice.invoice_number || '—'}</span>
          </td>
          <td className="table-data">
            <span className="text-sm" title={invoice.supplier_name}>
              {invoice.supplier_name || '—'}
            </span>
          </td>
          <td className="table-data">
            {getStatusBadge(
              parseInt(invoice.status, 10),
              invoice.status_display,
            )}
          </td>
          <td className="table-data">
            <div className="div-flex-row cg-5">
              <button
                className="btn btn-sm btn-light"
                onClick={() => handlePreview(invoice.attachment_url)}
                title="Preview"
                aria-label="Preview invoice"
              >
                <EyeIcon className="icon-size-lg" />
              </button>
              {canUploadInvoice && (
                <button
                  className="btn btn-sm btn-light"
                  onClick={() => setConfirmRemoveId(invoice.attachment_id)}
                  title="Remove"
                  aria-label="Remove invoice"
                >
                  <DeleteIcon className="icon-size-lg" />
                </button>
              )}
            </div>
          </td>
        </tr>
      ))}
    </tbody>
  );

  // --- Render ---
  return (
    <div className="invoice-tab">
      {/* Upload message */}
      {uploadMessage && (
        <div
          className={`alert ${
            uploadMessage.includes('uploaded') ||
            uploadMessage.includes('success')
              ? 'alert-success'
              : 'alert-danger'
          } py-2 mb-3`}
          role="alert"
        >
          {uploadMessage}
        </div>
      )}

      {loading && invoices.length === 0 ? (
        <div className="div-flex-column div-align-center rg-10 py-5">
          <div className="spinner-border" role="status" />
          <span className="text-sm">Loading invoices...</span>
        </div>
      ) : !hasInvoices ? (
        /* --- Empty state: centered like Add Vehicle Content --- */
        <div className="invoice-empty-state-container div-flex-center div-align-center rg-10">
          <div className="div-flex-column div-align-center rg-5">
            <span className="text-lg text-semi-bold">
              No invoices uploaded yet!
            </span>
            <span className="text-grey text-sm">
              Take a Photo or Upload an invoice image to extract line items
              using AI.
            </span>
          </div>
          {canUploadInvoice && (
            <UploadInvoiceComponent
              onFileUpload={handleFileUpload}
              uploading={uploading}
            />
          )}
        </div>
      ) : (
        /* --- Invoice list --- */
        <div>
          {/* Header row: Refresh */}
          <div className="div-flex-row div-flex-end div-align-center section-title">
            <button
              className="icon-btn-none"
              onClick={fetchInvoiceAttachments}
              title="Refresh"
              aria-label="Refresh invoice list"
            >
              <RefreshIcon className="icon-size-lg" color="#9F9F9F" />
            </button>
          </div>

          {/* Invoice table - resizable columns */}
          <div
            className={`table-fixed-container table-scrollable-container theme-scrollable-container-${themeColor}`}
          >
            <Table
              headers={invoiceTableHeaders}
              minCellWidth={100}
              tableContent={renderTableContent()}
              stickyHeader
            />
          </div>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Confirm Remove Modal */}
      <Modal
        show={Boolean(confirmRemoveId)}
        onHide={() => setConfirmRemoveId(null)}
        centered
      >
        <Modal.Body>
          <div className="d-flex flex-column rg-10">
            <div className="text-lg text-center text-semi-bold">
              Remove Invoice
            </div>
            <div className="text-sm text-center text-grey">
              Are you sure you want to remove this invoice? It will be excluded
              from suggestions.
            </div>
            <div className="div-flex-row div-space-between mt-3">
              <button
                className={`vehicle-tracking-detail-add-vehcile-data-btn theme-text-${themeColor} theme-border-color-${themeColor} div-align-center`}
                onClick={() => setConfirmRemoveId(null)}
              >
                <span className="text-md">Cancel</span>
              </button>
              <button
                className={`vehicle-tracking-detail-move-stage-btn div-align-center theme-background-${themeColor}`}
                onClick={handleRemoveConfirm}
              >
                <span className="text-md">Remove</span>
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Error Modal */}
      <Modal
        show={Boolean(errorMessage)}
        onHide={() => setErrorMessage('')}
        centered
      >
        <Modal.Body>
          <div className="d-flex flex-column rg-10">
            <div className="text-lg text-center text-semi-bold">Error</div>
            <div className="text-sm text-center text-grey">{errorMessage}</div>
            <div className="div-flex-row div-flex-end mt-3">
              <button
                className={`vehicle-tracking-detail-move-stage-btn div-align-center theme-background-${themeColor}`}
                onClick={() => setErrorMessage('')}
              >
                <span className="text-md">OK</span>
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
